import { useEffect, useMemo, useRef, useState, type TouchEvent } from "react";
import { addMonths, format, setMonth, setYear, startOfMonth } from "date-fns";
import { de } from "date-fns/locale";
import { useIsMobile } from "@/hooks/use-mobile";

export const MONTHS_TO_SHOW_DESKTOP = 6;
export const MONTHS_TO_SHOW_MOBILE = 1;
const MONTH_RENDER_BUFFER = 3;
const MOBILE_SWIPE_THRESHOLD = 60;

// Shared layout of the month-column calendars (projects, vehicles)
export const ROW_HEIGHT = 20;
export const DAY_NUM_WIDTH = 24;
export const DAY_ABBREV_WIDTH = 24;
export const HEADER_HEIGHT = 36;

export const MONTH_NAMES = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
];

// German holidays (simplified - major ones)
export const getHolidays = (year: number): Date[] => {
  return [
    new Date(year, 0, 1), // Neujahr
    new Date(year, 4, 1), // Tag der Arbeit
    new Date(year, 9, 3), // Tag der Deutschen Einheit
    new Date(year, 11, 25), // Weihnachten
    new Date(year, 11, 26), // 2. Weihnachten
  ];
};

export const getDayAbbrev = (date: Date): string => {
  return format(date, "EEEEEE", { locale: de });
};

/**
 * Month columns that scroll horizontally: which months are rendered and
 * visible, plus navigation (previous/next/today, month/year pickers on
 * mobile, swipe gestures). `monthWidth` is the pixel width of one column.
 */
export function useCalendarMonths(monthWidth: number) {
  const isMobile = useIsMobile();
  const monthsToShow = isMobile
    ? MONTHS_TO_SHOW_MOBILE
    : MONTHS_TO_SHOW_DESKTOP;
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const [startDate, setStartDate] = useState(() => startOfMonth(new Date()));
  const [renderedMonthCount, setRenderedMonthCount] = useState(
    () => monthsToShow,
  );
  const [visibleMonthRange, setVisibleMonthRange] = useState(() => ({
    startIdx: 0,
    endIdx: monthsToShow - 1,
  }));

  const months = useMemo(() => {
    return Array.from({ length: renderedMonthCount }, (_, i) =>
      addMonths(startDate, i),
    );
  }, [startDate, renderedMonthCount]);
  const visibleStartMonth = useMemo(
    () => addMonths(startDate, visibleMonthRange.startIdx),
    [startDate, visibleMonthRange.startIdx],
  );
  const visibleEndMonth = useMemo(
    () => addMonths(startDate, visibleMonthRange.endIdx),
    [startDate, visibleMonthRange.endIdx],
  );

  useEffect(() => {
    setRenderedMonthCount(
      isMobile ? monthsToShow : monthsToShow + MONTH_RENDER_BUFFER,
    );
    setVisibleMonthRange({ startIdx: 0, endIdx: monthsToShow - 1 });
  }, [isMobile, monthsToShow, startDate]);

  useEffect(() => {
    if (isMobile) return;

    const viewport = scrollAreaRef.current?.querySelector<HTMLElement>(
      "[data-radix-scroll-area-viewport]",
    );
    if (!viewport) return;

    viewport.scrollLeft = 0;
  }, [isMobile, startDate]);

  useEffect(() => {
    if (isMobile) return;

    const viewport = scrollAreaRef.current?.querySelector<HTMLElement>(
      "[data-radix-scroll-area-viewport]",
    );
    if (!viewport) return;

    const handleScroll = () => {
      const columnWidth = isMobile ? viewport.clientWidth : monthWidth;
      if (columnWidth <= 0) return;
      const visibleStartIdx = Math.max(
        0,
        Math.floor(viewport.scrollLeft / columnWidth),
      );
      const visibleEndIdx = Math.max(
        visibleStartIdx,
        Math.ceil((viewport.scrollLeft + viewport.clientWidth) / columnWidth) -
          1,
      );
      setVisibleMonthRange((range) =>
        range.startIdx === visibleStartIdx && range.endIdx === visibleEndIdx
          ? range
          : { startIdx: visibleStartIdx, endIdx: visibleEndIdx },
      );
      const visibleEndMonthCount = Math.ceil(
        (viewport.scrollLeft + viewport.clientWidth) / columnWidth,
      );
      const nextRenderedMonthCount = Math.max(
        monthsToShow + MONTH_RENDER_BUFFER,
        visibleEndMonthCount + MONTH_RENDER_BUFFER,
      );
      setRenderedMonthCount((count) =>
        nextRenderedMonthCount > count ? nextRenderedMonthCount : count,
      );
    };

    viewport.addEventListener("scroll", handleScroll, { passive: true });
    return () => viewport.removeEventListener("scroll", handleScroll);
  }, [isMobile, monthWidth, monthsToShow]);

  const handlePrevious = () => {
    if (!isMobile) {
      const viewport = scrollAreaRef.current?.querySelector<HTMLElement>(
        "[data-radix-scroll-area-viewport]",
      );

      if (viewport && visibleMonthRange.startIdx > 0) {
        viewport.scrollTo({
          left: Math.max(0, viewport.scrollLeft - monthWidth),
          behavior: "smooth",
        });
        return;
      }
    }

    setStartDate((prev) => addMonths(prev, -1));
  };

  const handleNext = () => {
    setStartDate((prev) => addMonths(prev, 1));
  };

  const handleTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    if (!isMobile) return;
    const touch = event.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
    if (!isMobile || !touchStartRef.current) return;
    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;
    touchStartRef.current = null;

    if (
      Math.abs(deltaX) < MOBILE_SWIPE_THRESHOLD ||
      Math.abs(deltaX) <= Math.abs(deltaY) * 1.5
    ) {
      return;
    }

    if (deltaX < 0) {
      handleNext();
    } else {
      handlePrevious();
    }
  };

  const handleMonthChange = (monthIndex: string) => {
    setStartDate((prev) => setMonth(prev, parseInt(monthIndex)));
  };

  const handleYearChange = (year: string) => {
    setStartDate((prev) => setYear(prev, parseInt(year)));
  };

  const handleToday = () => {
    setStartDate(startOfMonth(new Date()));
  };

  return {
    isMobile,
    scrollAreaRef,
    startDate,
    months,
    renderedMonthCount,
    visibleStartMonth,
    visibleEndMonth,
    handlePrevious,
    handleNext,
    handleToday,
    handleMonthChange,
    handleYearChange,
    handleTouchStart,
    handleTouchEnd,
  };
}
